import {useCallback, useEffect, useRef, useState} from 'react';
import {apiFetch} from '../api';
import Checklist from './Checklist';
import NewChecklistModal from './NewChecklistModal';

export default function ChecklistContainer() {
    const [checklistData, setChecklistData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [focusedId, setFocusedId] = useState(null);

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') setFocusedId(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    useEffect(() => {
        document.body.classList.toggle('focus-mode', focusedId !== null);
        return () => document.body.classList.remove('focus-mode');
    }, [focusedId]);

    const focusChecklist = useCallback((id) => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    // Ref keeps callbacks stable without stale closure issues
    const dataRef = useRef(checklistData);
    useEffect(() => { dataRef.current = checklistData; }, [checklistData]);

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklists', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setChecklistData(data.sort((a, b) => a.id - b.id)))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        return () => controller.abort();
    }, []);

    const putChecklist = useCallback(async (checklistId, updated) => {
        const res = await apiFetch(`/checklists/${checklistId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(updated),
        });
        const saved = await res.json();
        setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
    }, []);

    const deleteChecklist = useCallback(async (id) => {
        await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
        setChecklistData(prev => prev.filter(c => c.id !== id));
    }, []);

    const updateTitle = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }, [putChecklist]);

    const updateItem = useCallback((checklistId, itemId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, title} : i);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const deleteItem = useCallback((checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.filter(i => i.id !== itemId);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const addItem = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newItem = {id: Math.max(0, ...checklist.items.map(i => i.id)) + 1, title, checked: false};
        putChecklist(checklistId, {...checklist, items: [newItem, ...checklist.items]});
    }, [putChecklist]);

    const toggleItem = useCallback((checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, checked: !i.checked} : i);
        putChecklist(checklistId, {...checklist, items});
    }, [putChecklist]);

    const addNewChecklist = useCallback(async (title) => {
        const res = await apiFetch('/checklists', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({title, items: []}),
        });
        const created = await res.json();
        setChecklistData(prev => [...prev, created]);
        setFocusedId(created.id);
    }, []);

    const handleModalConfirm = useCallback((title) => {
        addNewChecklist(title);
        setShowModal(false);
    }, [addNewChecklist]);

    return (
        <div>
            <div>
                <button className="add-checklist-button" onClick={() => setShowModal(true)}>
                    + New List
                </button>
            </div>
            {showModal ? (
                <NewChecklistModal onConfirm={handleModalConfirm} onCancel={() => setShowModal(false)}/>
            ) : null}
            {focusedId !== null && (
                <div className="focus-backdrop" onClick={() => setFocusedId(null)} />
            )}
            <div className={`checklists-container${focusedId !== null ? ' checklists-container--hidden' : ''}`}>
                {checklistData.map(checklist => (
                    <Checklist
                        key={checklist.id}
                        data={checklist}
                        onDelete={deleteChecklist}
                        onAddItem={addItem}
                        onToggleItem={toggleItem}
                        onDeleteItem={deleteItem}
                        onUpdateTitle={updateTitle}
                        onUpdateItem={updateItem}
                        isFocused={focusedId === checklist.id}
                        onFocus={focusChecklist}
                        onUnfocus={unfocusChecklist}
                    />
                ))}
            </div>
        </div>
    );
}
